import { useEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { isTruncated, Tooltip } from '@ics-portal/guardian';

export const OVERFLOW_CELL_CLASS = 'ics-table__cell--overflow';

const OVERFLOW_CELL_SELECTOR = `.${OVERFLOW_CELL_CLASS}`;

interface HoveredCell {
    cell: HTMLElement;
    text: string;
}

export interface OverflowTooltipHandlers {
    onMouseOver: (event: MouseEvent<HTMLElement>) => void;
    onMouseOut: (event: MouseEvent<HTMLElement>) => void;
}

export interface CellOverflowTooltip {
    handlers: OverflowTooltipHandlers;
    tooltip: ReactElement;
}

function findOverflowCell(node: EventTarget | null): HTMLElement | null {
    if (!(node instanceof Element)) {
        return null;
    }
    return node.closest<HTMLElement>(OVERFLOW_CELL_SELECTOR);
}

/**
 * One tooltip for the whole table. Hover is delegated from the table wrapper;
 * the hovered cell becomes the PrimeReact target only while it is truncated,
 * so no per-cell listeners or attributes exist.
 *
 * PrimeReact binds its own mouseenter/mouseleave listeners to the target after
 * the commit that sets it, which is too late for the mouseenter that already
 * happened. The effect replays that one event; leaving the cell is handled by
 * PrimeReact's own mouseleave listener.
 */
export function useCellOverflowTooltip(): CellOverflowTooltip {
    const [hovered, setHovered] = useState<HoveredCell | null>(null);
    const tooltipRef = useRef<Tooltip>(null);

    useEffect(() => {
        if (!hovered) {
            tooltipRef.current?.hide();
            return;
        }
        hovered.cell.dispatchEvent(new MouseEvent('mouseenter'));
    }, [hovered]);

    function onMouseOver(event: MouseEvent<HTMLElement>): void {
        const cell = findOverflowCell(event.target);
        if (!cell) {
            return;
        }
        if (hovered?.cell === cell) {
            return;
        }
        const text = cell.textContent.trim();
        if (!text || !isTruncated(cell)) {
            setHovered(null);
            return;
        }
        setHovered({ cell, text });
    }

    function onMouseOut(event: MouseEvent<HTMLElement>): void {
        const cell = findOverflowCell(event.target);
        if (!cell || cell === findOverflowCell(event.relatedTarget)) {
            return;
        }
        setHovered(null);
    }

    const tooltip = (
        <Tooltip
            ref={tooltipRef}
            target={hovered?.cell}
            content={hovered?.text}
            className="ics-overflow-tooltip"
            position="bottom"
            mouseTrack={false}
        />
    );

    return { handlers: { onMouseOver, onMouseOut }, tooltip };
}
